import { useGenerateAccessTicket, useSSOConnections } from "@/api/sso";
import { useOrgUsers } from "@/api/users";
import { Button } from "@/components/ui/button";
import { formatProvider } from "@/utils/authProvider";
import { Plus } from "lucide-react";
import {
  chipButtonClassName,
  LcBadge,
  SettingRow,
  smButtonClassName,
} from "./components/manage-pane-primitives";

/**
 * Single sign-on, as rows inside the Organization scope's Workspace section —
 * it is how this organization's people sign in, not a place of its own.
 * Enterprise-only; setup and changes run through the identity provider's
 * self-service portal, so every verb here mints a ticket and hands off.
 */
export function OrgSSOSection() {
  const { data: connections = [] } = useSSOConnections();
  const { data: users } = useOrgUsers();
  const { mutate: generateTicket, isPending } = useGenerateAccessTicket({
    onSuccess: (data) => window.open(data.ticket_url, "_blank"),
  });

  if (connections.length === 0) {
    return (
      <SettingRow
        label="Single sign-on"
        desc="Let your team sign in with your identity provider — Okta, Entra ID, Google Workspace, and any OIDC or SAML provider."
      >
        <Button
          size="sm"
          variant="secondary"
          className={smButtonClassName}
          onClick={() => generateTicket(undefined)}
          disabled={isPending}
          isLoading={isPending}
        >
          <Plus />
          Set up
        </Button>
      </SettingRow>
    );
  }

  return (
    <>
      {connections.map((connection) => {
        const signedIn =
          users?.filter((user) => user.provider === connection.strategy)
            .length ?? 0;

        return (
          <SettingRow
            key={connection.id}
            label="Single sign-on"
            desc={`${formatProvider(connection.strategy)} · ${signedIn} ${
              signedIn === 1 ? "person" : "people"
            } signed in with this connection`}
          >
            <LcBadge>Active</LcBadge>
            <Button
              size="sm"
              variant="ghost"
              className={chipButtonClassName}
              onClick={() => generateTicket(connection.id)}
              disabled={isPending}
            >
              Manage
            </Button>
          </SettingRow>
        );
      })}
      <p className="px-2.5 pt-1 text-sm text-n-50">
        To remove a connection, email{" "}
        <a href="mailto:support@cohesive.ai" className="underline">
          support@cohesive.ai
        </a>
        .
      </p>
    </>
  );
}
